import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, ShieldOff, Clock, AlertCircle, Lock } from 'lucide-react';
import BrokerBadge from '../components/BrokerBadge';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PiiShareView() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    fetch(`${API}/api/pii-share/view/${token}`)
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          setState({ loading: false, error: body.detail || `Error ${r.status}`, data: null });
          return;
        }
        const data = await r.json();
        setState({ loading: false, error: null, data });
      })
      .catch(() => setState({ loading: false, error: 'Network error', data: null }));
  }, [token]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/assagart-logo.png?v=2" alt="Assagart" className="h-12 mx-auto mb-4 opacity-80" />
          <p className="text-gold uppercase tracking-[0.3em] text-[10px]">Secure One-time Share</p>
        </div>

        {state.loading && (
          <div className="luxury-card p-10 text-center" data-testid="share-view-loading">
            <Lock size={28} className="text-gold mx-auto mb-3 animate-pulse" />
            <p className="text-muted-foreground text-sm">Verifying secure link...</p>
          </div>
        )}

        {state.error && (
          <div className="luxury-card p-8 text-center border-red-500/30" data-testid="share-view-error">
            <ShieldOff size={32} className="text-red-400 mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-white mb-3">Link unavailable</h1>
            <p className="text-sm text-zinc-400">{state.error}</p>
            <p className="text-xs text-muted-foreground mt-6">
              This may be because the link has already been used, has expired, or was revoked by the owner.
              Please request a new share link from the asset owner.
            </p>
          </div>
        )}

        {state.data && (
          <div className="luxury-card p-8" data-testid="share-view-ok">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#1A1A1A]">
              <div className="w-12 h-12 rounded bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <ShieldCheck size={20} className="text-emerald-400" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-0.5">One-time view</p>
                <h1 className="font-serif text-xl text-white">{state.data.owner_name}</h1>
                <p className="text-[11px] text-muted-foreground">
                  shared {state.data.fields.length} field{state.data.fields.length === 1 ? '' : 's'} with {state.data.recipient_name || state.data.recipient_email}
                </p>
                <div className="mt-2">
                  <BrokerBadge email={state.data.recipient_email} size="md" />
                </div>
              </div>
            </div>

            {state.data.purpose && (
              <div className="bg-gold/5 border border-gold/15 rounded p-3 mb-5">
                <p className="text-[10px] uppercase tracking-wider text-gold mb-1">Purpose</p>
                <p className="text-sm text-white">{state.data.purpose}</p>
              </div>
            )}

            <div className="space-y-3 mb-6" data-testid="share-fields">
              {state.data.fields.map(f => (
                <div key={f.key} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded p-3" data-testid={`share-field-${f.key}`}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{f.label}</p>
                  <p className="text-white text-base font-mono break-all">{f.value}</p>
                </div>
              ))}
            </div>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-3 flex items-start gap-2 text-[11px] text-amber-100/80">
              <AlertCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-300 mb-1">This is a one-time view</p>
                <p>{state.data.notice}</p>
                <p className="mt-1 flex items-center gap-1">
                  <Clock size={11} /> Viewed at {new Date(state.data.viewed_at).toLocaleString()}
                  · Link expires {new Date(state.data.expires_at).toLocaleString()}
                </p>
              </div>
            </div>

            <p className="text-center text-[10px] text-muted-foreground mt-6">
              You will not be able to revisit this page. Please save the information you need now.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
